import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useCallback } from "react";
import {
  ActionItemCollapsibleFragmentFragment,
  ActionItemState,
  GetActionItemsCollapsibleQueryQuery,
  GetActionItemsCollapsibleQueryQueryVariables,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getActionItemsCollapsibleQuery from "./graphql/get-action-items-collapsible-query";

export const dashboardActionItemsFirstPageLimit = 5;

const ActionItemsCollapsibleContainer = ({
  actionItemState,
  uiPreferenceCacheNamespace,
  path = "",
  queryVariables,
  filterResults,
}: {
  actionItemState: Pick<ActionItemState, "label" | "value">;
  uiPreferenceCacheNamespace: keyof UiPreferenceCache;
  path?: string;
  queryVariables: GetActionItemsCollapsibleQueryQueryVariables;
  filterResults?: (
    variables: GetActionItemsCollapsibleQueryQueryVariables,
  ) => (node: ActionItemCollapsibleFragmentFragment) => boolean;
}) => {
  const organization = currentOrganizationVar();

  // HOOKS
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const open =
    uiPreferenceCache[`${uiPreferenceCacheNamespace}`] === null ||
    uiPreferenceCache[`${uiPreferenceCacheNamespace}`].includes(
      actionItemState.value,
    );

  const limit = 20;

  const { loading, data, fetchMore } = useQuery<
    GetActionItemsCollapsibleQueryQuery,
    GetActionItemsCollapsibleQueryQueryVariables
  >(getActionItemsCollapsibleQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...queryVariables,
      fetchData: open,
    },
  });

  const totalCount = data?.artifacts?.totalCount;
  const artifacts = data?.artifacts?.edges
    ? assertEdgesNonNull(data.artifacts).filter(
        (d) => d.__typename === "ActionItemArtifactNode",
      )
    : [];
  const actionItems = artifacts;
  const filteredActionItems = filterResults
    ? actionItems.filter((actionItem) =>
        filterResults(queryVariables)(actionItem),
      )
    : actionItems;

  const handleClickMore = () => {
    fetchMore({
      variables: {
        merge: true,
        after: data?.artifacts?.pageInfo.endCursor,
        first: limit,
      },
    });
  };

  const handleToggleCollapsible = useCallback(
    (isOpen: boolean) => {
      const allActionItemStates = compact(organization.actionItemStates).map(
        ({ value }) => value,
      );
      const states =
        uiPreferenceCache[`${uiPreferenceCacheNamespace}`] ||
        allActionItemStates;
      const newVal = isOpen
        ? states.concat(actionItemState.value)
        : states.filter((state: number) => actionItemState.value !== state);
      saveUiPreference({ [`${uiPreferenceCacheNamespace}`]: newVal });
    },
    [
      actionItemState.value,
      organization.actionItemStates,
      saveUiPreference,
      uiPreferenceCache,
      uiPreferenceCacheNamespace,
    ],
  );

  return (
    <div
      className="relative"
      aria-label={`${actionItemState.label.toLowerCase()} action items container`}
    >
      <Layout.SidebarSubSection
        title={actionItemState.label}
        isExpanded={open}
        onToggleIsExpanded={handleToggleCollapsible}
        className="relative"
        loading={filteredActionItems.length === 0 && loading}
        count={totalCount}
      >
        <Layout.SidebarSubSectionList aria-label="Action items collapsible container">
          {filteredActionItems.map((actionItem) => (
            <Layout.SidebarSubSectionListItem
              key={actionItem.id}
              className={classNames("relative group")}
            >
              <Artifact artifact={actionItem} urlPrefix={path} />
            </Layout.SidebarSubSectionListItem>
          ))}
          <Layout.SidebarSubSectionListPaginate
            loading={loading}
            hasNextPage={!!data?.artifacts?.pageInfo.hasNextPage}
            onClickMore={handleClickMore}
          />
        </Layout.SidebarSubSectionList>
      </Layout.SidebarSubSection>
    </div>
  );
};

export default ActionItemsCollapsibleContainer;
